import React, { useState } from "react";
import {
  AssignmentTurnedInOutlined,
  InfoOutlined,
  Label,
  Print,
  Receipt,
  RequestQuote,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useAuth, useTablePagination } from "hooks";
import { useVendorProfileDetailContext } from "contexts/VendorProfileDetailContext";
import { useLazyListVendorShipmentsQuery } from "store/services/vendorProfileApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import ExportVendorShipment from "./ExportVendorShipment";
import GenerateShipmentLabel from "screens/App/CRM/ShipmentRequest/ShipmentComponents/GenerateShipmentLabel";
import ShipmentInvoicePrint2 from "screens/App/CRM/ShipmentRequest/ShipmentComponents/ShipmentInvoicePrint2";
import { generatePodPrint } from "screens/App/CRM/ShipmentRequest/ShipmentComponents/ShipmentPodPrint";
import { generatePORPrint } from "screens/App/CRM/ShipmentRequest/ShipmentComponents/ShipmentPORPrint";
import {
  useLazyGetShipmentDetailQuery,
  useLazyListShipmentPricesQuery,
} from "store/services/shipmentApi";
import { useGetDeliveryDetailByShipmentIdQuery } from "store/services/deliveryApi";

const PodPrintTrigger = ({ shipment, onDone }) => {
  const { data: deliveryDetail, isLoading, isSuccess, isError } =
    useGetDeliveryDetailByShipmentIdQuery(shipment?._id, {
      skip: !shipment?._id,
    });

  React.useEffect(() => {
    if (isLoading || (!isSuccess && !isError)) return;
    generatePodPrint({ ShipmentDetail: shipment }, deliveryDetail);
    onDone();
  }, [isLoading, isSuccess, isError, deliveryDetail, shipment, onDone]);

  return null;
};

const ShipmentRowPrintActions = ({ shipment }) => {
  const { isDriver, isCustomer, loggedInUser, viewEntity } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [podRequested, setPodRequested] = useState(false);

  const [fetchShipmentDetail, { data: shipmentDetail, isLoading: loadingShipmentDetail }] =
    useLazyGetShipmentDetailQuery();

  const [fetchPrices, { data: pricesRes, isLoading: loadingPrices }] =
    useLazyListShipmentPricesQuery();

  if (isDriver || isCustomer) return null;

  const isPrintActive = Boolean(anchorEl) || showLabelModal || showInvoiceModal;

  const printShipment = shipmentDetail || shipment;

  const handlePrintClick = (event) => {
    if (shipment?._id) {
      fetchShipmentDetail(shipment._id);
    }
    setAnchorEl(event.currentTarget);
  };

  const handleGeneratePor = () => {
    generatePORPrint(printShipment);
  };

  const handleInvoicePrint = () => {
    if (shipment?._id) {
      fetchPrices({ shipmentId: shipment._id, limit: 100 });
    }
    setShowInvoiceModal(true);
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label="Print Shipment"
        color={isPrintActive ? "secondary" : "primary"}
        disabled={shipment?.isActive === false}
        onClick={handlePrintClick}
      >
        <Print />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        className="mt-3"
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          onClick={() => {
            setShowLabelModal(true);
            setAnchorEl(null);
          }}
          disabled={loadingShipmentDetail || !shipmentDetail}
        >
          <Label sx={{ mr: 1 }} /> Generate Shipment Label
        </MenuItem>
        <MenuItem
          onClick={handleInvoicePrint}
          disabled={loadingShipmentDetail || !shipmentDetail}
        >
          <RequestQuote sx={{ mr: 1 }} /> Invoice Print
        </MenuItem>
        {shipment?.status === "Delivered" && (
          <MenuItem
            onClick={() => {
              setPodRequested(true);
              setAnchorEl(null);
            }}
            disabled={loadingShipmentDetail || !shipmentDetail}
          >
            <AssignmentTurnedInOutlined sx={{ mr: 1 }} /> Proof of Delivery
            (POD)
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleGeneratePor();
            setAnchorEl(null);
          }}
          disabled={loadingShipmentDetail || !shipmentDetail}
        >
          <Receipt sx={{ mr: 1 }} /> Proof of Receipt (POR)
        </MenuItem>
      </Menu>
      {podRequested && (
        <PodPrintTrigger
          shipment={printShipment}
          onDone={() => setPodRequested(false)}
        />
      )}
      {shipment && (
        <GenerateShipmentLabel
          isOpen={showLabelModal && Boolean(shipmentDetail)}
          onClose={() => setShowLabelModal(false)}
          shipment={printShipment}
        />
      )}
      {showInvoiceModal && (
        <ShipmentInvoicePrint2
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          ShipmentDetail={printShipment}
          pricesRes={pricesRes}
          loadingPrices={loadingPrices}
          entity={viewEntity?.entity}
          loggedInUser={loggedInUser}
        />
      )}
    </>
  );
};

const VendorShipmentList = () => {
  const { vendorProfileDetail } = useVendorProfileDetailContext();
  const { isVendor, isCustomer, viewEntity } = useAuth();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [statusFilter, setStatusFilter] = useState("All");
  const [sourceBranch, setSourceBranch] = useState("");
  const [destinationBranch, setDestinationBranch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [triggerGetDDLBranchList, { data: branchesData }] =
    useLazyGetDDLBranchListQuery();

  const statusOptions = [
    "All",
    "Pending",
    "Collected",
    "Consolidation",
    "Loaded",
    "Unloaded",
    "Deconsolidation",
    "CrossDocked",
    "SendOut",
    "Warehousing",
    "Delivered",
    "DeliveryFailed",
    "Return",
    "Cancelled",
  ];

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.shipmentId,
      cell: (row) => row?.shipmentId,
      width: "150px",
    },
    {
      name: "Sender",
      selector: (row) =>
        row?.customerUser?.name || row?.vendor?.shopName || "N/A",
      cell: (row) => (
        <Box
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row?.customerUser?.name || row?.vendor?.shopName || "N/A"}
        </Box>
      ),
      omit: isVendor,
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.customerUser?.name,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.customerUser?.name}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.receiver?.phone}
          </span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch?.name || "Loading...",
      cell: (row) => (
        <span>{row?.sourceBranch?.name || <CircularProgress size={12} />}</span>
      ),
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch?.name || "Loading...",
      cell: (row) => (
        <span>
          {row?.destinationBranch?.name || <CircularProgress size={12} />}
        </span>
      ),
    },
    {
      name: "Created Ats",
      selector: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      center: true,
      cell: (row) => {
        const statusColors = {
          Pending: "bg-yellow-700 text-white",
          Collected: "bg-gray-500 text-white",
          Consolidation: "bg-gray-500 text-white",
          Loaded: "bg-gray-500 text-white",
          Unloaded: "bg-gray-500 text-white",
          Deconsolidation: "bg-gray-500 text-white",
          CrossDocked: "bg-gray-500 text-white",
          SendOut: "bg-gray-500 text-white",
          Warehousing: "bg-gray-500 text-white",
          Delivered: "bg-green-700 text-white",
          DeliveryFailed: "bg-red-700 text-white",
          Return: "bg-orange-700 text-white",
          Cancelled: "bg-red-700 text-white",
        };
        return (
          <Badge
            className={`rounded-md px-1 py-1 ${statusColors[row.status] || "bg-gray-500 text-white"
              }`}
          >
            {row.status}
          </Badge>
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = isVendor
          ? "/app/vendor/shipment"
          : isCustomer
            ? "/app/customer/shipment"
            : "/app/crm/shipment";
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <Link to={`${basePath}/${row?._id}/detail`}>
              <IconButton
                size="small"
                aria-label="View Shipment"
                color="primary"
              >
                <InfoOutlined />
              </IconButton>
            </Link>
            <ShipmentRowPrintActions shipment={row} />
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchShipments, { data: shipmentRes, isLoading, isFetching }] =
    useLazyListVendorShipmentsQuery();

  React.useEffect(() => {
    if (!vendorProfileDetail?._id) return;

    fetchShipments({
      vendorId: vendorProfileDetail?._id,
      page: currentPage,
      limit: perPage,
      ...(statusFilter !== "All" && { status: statusFilter }),
      ...(sourceBranch && { sourceBranch: sourceBranch?._id }),
      ...(destinationBranch && { destinationBranch: destinationBranch?._id }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
    });

    triggerGetDDLBranchList({
      account: viewEntity.entity.account._id,
      entity: viewEntity.entity._id,
    });
  }, [
    currentPage,
    vendorProfileDetail?._id,
    fetchShipments,
    perPage,
    statusFilter,
    triggerGetDDLBranchList,
    viewEntity.entity.account._id,
    viewEntity.entity._id,
    sourceBranch,
    destinationBranch,
    fromDate,
    toDate,
  ]);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1}
      >
        <Typography variant="h6">Shipment List</Typography>

        <Box display="flex" alignItems="center" flexShrink={0} gap={1}>
          <BaseSelect
            label="Status"
            options={statusOptions}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-48"
          />

          <BaseAutocomplete
            label="Source Branch"
            placeholder="Select Source Branch"
            options={branchesData || []}
            getOptionLabel={(option) => option && option.name}
            isOptionEqualTo={(option, value) => option?._id === value?._id}
            value={sourceBranch}
            onChange={(_, data) => setSourceBranch(data)}
            className="w-48"
          />

          <BaseAutocomplete
            label="Destination Branch"
            placeholder="Select Destination Branch"
            options={branchesData || []}
            getOptionLabel={(option) => option && option.name}
            isOptionEqualTo={(option, value) => option?._id === value?._id}
            value={destinationBranch}
            onChange={(_, data) => setDestinationBranch(data)}
            className="w-52"
          />

          <BaseTextField
            label="From Date"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-48"
          />

          <BaseTextField
            label="To Date"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-48"
          />
        </Box>
        <ExportVendorShipment/>
      </Box>

      <Divider />

      <AppTable
        columns={columns}
        data={shipmentRes?.data || []}
        progressPending={isLoading || isFetching}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={shipmentRes?.totalItems || 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
      />
    </>
  );
};

export default VendorShipmentList;
